import { supabase } from '../lib/supabase';
import { EventAttendee } from '../types/event';
import * as crypto from 'crypto-js';

/**
 * Service de sécurité pour la prévention de la fraude des tickets
 */

// Clé secrète pour la génération des jetons de validation
const SECRET_KEY = 'VotAfrica_SecureTicket_2025';

// Durée de validité d'un jeton de validation (en secondes)
const TOKEN_VALIDITY_DURATION = 30; // 30 secondes

/**
 * Génère un jeton de validation temporaire pour un ticket
 * @param qrCode Code QR du ticket
 * @returns Jeton de validation avec timestamp
 */
export const generateValidationToken = (qrCode: string): string => {
  const timestamp = Math.floor(Date.now() / 1000); // Timestamp actuel en secondes
  const payload = `${qrCode}:${timestamp}`;
  const hmac = crypto.HmacSHA256(payload, SECRET_KEY);
  const token = `${payload}:${hmac}`;
  return btoa(token); // Encodage en base64
};

/**
 * Vérifie la validité d'un jeton de validation
 * @param token Jeton de validation à vérifier
 * @returns Objet contenant le résultat de la validation et le code QR extrait
 */
export const verifyValidationToken = (token: string): { valid: boolean; qrCode?: string; reason?: string } => {
  try {
    // Décoder le token
    const decodedToken = atob(token);
    const [qrCode, timestampStr, receivedHmac] = decodedToken.split(':');
    
    // Vérifier que tous les éléments sont présents
    if (!qrCode || !timestampStr || !receivedHmac) {
      return { valid: false, reason: 'Format de jeton invalide' };
    }
    
    // Vérifier la signature HMAC
    const payload = `${qrCode}:${timestampStr}`;
    const expectedHmac = crypto.HmacSHA256(payload, SECRET_KEY).toString();
    
    if (receivedHmac !== expectedHmac) {
      return { valid: false, reason: 'Signature invalide' };
    }
    
    // Vérifier l'expiration du jeton
    const timestamp = parseInt(timestampStr, 10);
    const currentTime = Math.floor(Date.now() / 1000);
    
    if (currentTime - timestamp > TOKEN_VALIDITY_DURATION) {
      return { valid: false, reason: 'Jeton expiré' };
    }
    
    // Jeton valide
    return { valid: true, qrCode };
  } catch (error) {
    return { valid: false, reason: 'Erreur lors de la validation du jeton' };
  }
};

/**
 * Enregistre une tentative de validation de ticket
 * @param qrCode Code QR du ticket
 * @param success Succès ou échec de la validation
 * @param deviceInfo Informations sur l'appareil utilisé pour la validation
 */
export const logTicketValidationAttempt = async (
  qrCode: string, 
  success: boolean, 
  deviceInfo: { 
    ip?: string; 
    userAgent?: string;
    location?: string;
  }
) => {
  try {
    await supabase.from('ticket_validation_logs').insert({
      qr_code: qrCode,
      success,
      device_info: deviceInfo,
      timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Erreur lors de l\'enregistrement de la tentative de validation:', error);
  }
};

/**
 * Vérifie si un ticket a déjà été utilisé et détecte les tentatives de fraude
 * @param qrCode Code QR du ticket
 * @returns Informations sur la validité du ticket
 */
export const checkTicketFraud = async (qrCode: string): Promise<{
  valid: boolean;
  attendee?: EventAttendee;
  fraudDetected: boolean;
  reason?: string;
}> => {
  try {
    // Récupérer les informations du ticket
    const { data: attendee, error } = await supabase
      .from('event_attendees')
      .select(`
        *,
        event:event_attendees_event_id_fkey(id, title, start_date, end_date),
        user:event_attendees_user_id_fkey(id, name, email),
        ticket_order:event_attendees_ticket_order_id_fkey(
          id,
          ticket_id,
          amount,
          ticket:ticket_orders_ticket_id_fkey(id, name, ticket_type, price, currency)
        )
      `)
      .eq('qr_code', qrCode)
      .single();

    if (error) {
      return { valid: false, fraudDetected: false, reason: 'Ticket introuvable' };
    }

    // Vérifier si le ticket a déjà été utilisé
    if (attendee.status === 'checked_in') {
      // Récupérer les logs de validation pour ce ticket
      const { data: logs } = await supabase
        .from('ticket_validation_logs')
        .select('*')
        .eq('qr_code', qrCode)
        .order('timestamp', { ascending: false })
        .limit(5);

      // Détecter les tentatives de réutilisation multiples (possible fraude)
      const recentLogs = logs?.filter(log => {
        const logTime = new Date(log.timestamp).getTime();
        const currentTime = Date.now();
        return (currentTime - logTime) < 3600000; // Logs des dernières 60 minutes
      }) || [];

      const fraudDetected = recentLogs.length >= 3;

      if (fraudDetected) {
        // Marquer le ticket comme potentiellement frauduleux
        await supabase
          .from('event_attendees')
          .update({ fraud_flag: true })
          .eq('id', attendee.id);
      }

      return { 
        valid: false, 
        attendee: attendee as EventAttendee, 
        fraudDetected,
        reason: 'Ce ticket a déjà été utilisé'
      };
    }

    // Vérifier si l'événement est terminé
    if (attendee.event && new Date(attendee.event.end_date) < new Date()) {
      return { 
        valid: false, 
        attendee: attendee as EventAttendee, 
        fraudDetected: false,
        reason: 'L\'événement est terminé'
      };
    }

    // Vérifier si l'événement n'a pas encore commencé
    if (attendee.event && new Date(attendee.event.start_date) > new Date()) {
      // Ce n'est pas une fraude, mais le ticket n'est pas encore valide
      return { 
        valid: false, 
        attendee: attendee as EventAttendee, 
        fraudDetected: false,
        reason: 'L\'événement n\'a pas encore commencé'
      };
    }

    // Ticket valide
    return { valid: true, attendee: attendee as EventAttendee, fraudDetected: false };
  } catch (error) {
    console.error('Erreur lors de la vérification de fraude:', error);
    return { valid: false, fraudDetected: false, reason: 'Erreur lors de la vérification' };
  }
};
